var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useLocation } from "react-router-dom";
import { sanitize } from "isomorphic-dompurify";
import { DEVICE_SERIAL_NUMBER_QUERY_PARAM, getAgeVerificationUuid, getIsOfflineRegistrationAvForced, getIsRedirectToDashboardAfterRegistration, getPassVerifiedData, isNonVerifiedOfflineAvVariant, ISO_DATE_FORMAT, isOfflineRegistrationVariant, PASS_VERIFIED_DATE_OF_BIRTH_COOKIE, PASS_VERIFIED_NAME, PASS_VERIFIED_PHONE_COOKIE, PERSONAL_INFORMATION, removeSpacesFromCode, translateBackendErrorCode, } from "@jti/fe-common";
import { useMessageBus } from "@jti/fe-message-bus";
import { BrowserStorage, useAvpActions, User } from "@jti/rest-api";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";
import { useConfig } from "@jti/ui/providers/useConfig";
import { useTranslations } from "@jti/ui/providers/useTranslations";
import { useAemModel } from "../../../../../contexts/AemModelContext";
import { useAemLanguagesModel } from "../../../../../contexts/LanguagesContext";
import { useAccountCancelation, useLogin, useRegister } from "../../../../../hooks/useAuth";
import { useCustomAttributes } from "../../../../../hooks/useCustomAttributes";
import { useDeviceRegistrationModel } from "../../../../../hooks/useDeviceRegistrationModel/useDeviceRegistrationModel";
import { getUsersToken, useDevices } from "../../../../../hooks/useDevices";
import { useHiddenAgeVerification } from "../../../../../hooks/useHiddenAgeVerification";
import { useProfile } from "../../../../../hooks/useProfile";
import { useProfileRedirect } from "../../../../../hooks/useProfileRedirect";
import { useSearchParam } from "../../../../../hooks/useSearchParam";
import { getInitialFormValues } from "../../utils/getInitialFormValues/getInitialFormValues";
import { prepareFieldsForAV } from "../../utils/prepareFieldsForAv/prepareFieldsForAv";
import { transformToCasUser } from "../../utils/transformToCasStructure/transformToCasStructure";
const getCurrentLanguage = (languages) => {
    var _a;
    return (_a = languages.find(({ current }) => current)) === null || _a === void 0 ? void 0 : _a.associatedCookieValue;
};
export const useRegistrationForm = ({ registrationFlowName, currentStep, totalSteps, code, nextStep, }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isUserCreated, setIsUserCreated] = useState(false);
    const config = useConfig();
    const languages = useAemLanguagesModel();
    const { search } = useLocation();
    const { __ } = useTranslations();
    const deviceSerialFromUrl = useSearchParam(DEVICE_SERIAL_NUMBER_QUERY_PARAM);
    const { registerDevice, unassignDeviceFromGuest, registerDeviceToGuest, assignDeviceToUser } = useDevices();
    const { isAgeVerificationHidden } = useHiddenAgeVerification();
    const { verify } = useAvpActions();
    const { register } = useRegister();
    const { login } = useLogin();
    const { cancelUserAccount } = useAccountCancelation();
    const { pushToSuccessfullRegistration: pushAfterSuccessfullRegistration, pushToSuccessfullOfflineRegistration } = useProfileRedirect();
    const { offlineRegistration, registrationCommon } = useAemModel();
    const { additionalFields } = useCustomAttributes();
    const { getUserProfile } = useProfile();
    const messageBus = useMessageBus();
    const [offlineRegistrationData, setOfflineRegistrationData] = useState();
    const { isAgeVerificationUuidFromCookieRequired } = registrationCommon || {
        isAgeVerificationUuidFromCookieRequired: false,
    };
    const isDeviceRegistrationEnabled = config.REGISTRATION_DEVICE_ENABLED === "true";
    const { isDeviceRegistrationMandatory: isDeviceRegistrationMandatoryFromModel } = useDeviceRegistrationModel();
    const isDeviceRegistrationMandatory = isDeviceRegistrationMandatoryFromModel && isDeviceRegistrationEnabled;
    const form = useForm({
        defaultValues: getInitialFormValues({ registrationFlowName, code, dobFormType: config.dobFormType }),
        shouldUnregister: false,
    });
    const showToastError = (error) => {
        const errorMessage = translateBackendErrorCode(error === null || error === void 0 ? void 0 : error.error_code, error === null || error === void 0 ? void 0 : error.message);
        if (errorMessage) {
            toast.error(_jsx("div", { dangerouslySetInnerHTML: { __html: sanitize(errorMessage) } }));
        }
        else if (Object.values(error === null || error === void 0 ? void 0 : error.errors).length) {
            const errorMessageCombined = Object.values(error === null || error === void 0 ? void 0 : error.errors).join(", ");
            toast.error(_jsx("div", { dangerouslySetInnerHTML: { __html: sanitize(errorMessageCombined) } }));
        }
    };
    const proceedWithRegistration = useCallback((additionalQueryParameters) => {
        const isOfflineDeviceRegistrationEnabled = (offlineRegistration === null || offlineRegistration === void 0 ? void 0 : offlineRegistration.isDeviceRegistrationEnabled) && isNonVerifiedOfflineAvVariant();
        const isRedirectToDashboard = getIsRedirectToDashboardAfterRegistration() && currentStep === totalSteps;
        const shouldShowQr = isOfflineRegistrationVariant() && additionalQueryParameters && currentStep === totalSteps;
        const isDeviceRegistrationStepEnabled = isDeviceRegistrationEnabled || isOfflineDeviceRegistrationEnabled;
        if (isRedirectToDashboard) {
            return pushAfterSuccessfullRegistration();
        }
        if (shouldShowQr) {
            return pushToSuccessfullOfflineRegistration(additionalQueryParameters);
        }
        if (isOfflineRegistrationVariant() && additionalQueryParameters) {
            setOfflineRegistrationData(additionalQueryParameters);
        }
        if (isDeviceRegistrationStepEnabled && !deviceSerialFromUrl && currentStep !== totalSteps) {
            nextStep();
        }
        else {
            pushAfterSuccessfullRegistration(search, isDeviceRegistrationMandatory);
        }
    }, [
        offlineRegistration === null || offlineRegistration === void 0 ? void 0 : offlineRegistration.isDeviceRegistrationEnabled,
        currentStep,
        totalSteps,
        isDeviceRegistrationEnabled,
        deviceSerialFromUrl,
        registrationFlowName,
        pushToSuccessfullOfflineRegistration,
        nextStep,
        pushAfterSuccessfullRegistration,
        search,
        isDeviceRegistrationMandatory,
    ]);
    const pushToUserQrCode = useCallback(() => {
        if (offlineRegistrationData) {
            pushToSuccessfullOfflineRegistration(offlineRegistrationData);
        }
    }, [offlineRegistrationData, pushToSuccessfullOfflineRegistration]);
    const clearRegistrationAssignments = useCallback((_a) => __awaiter(void 0, [_a], void 0, function* ({ deviceId, consumerEmail, loggedInUser }) {
        const clearRegistrationDataCalls = [];
        if ((loggedInUser === null || loggedInUser === void 0 ? void 0 : loggedInUser.ciam_token) && loggedInUser.uuid) {
            clearRegistrationDataCalls.push(cancelUserAccount(loggedInUser.uuid, loggedInUser.ciam_token));
        }
        if (deviceId && consumerEmail) {
            clearRegistrationDataCalls.push(unassignDeviceFromGuest(removeSpacesFromCode(deviceId), consumerEmail));
        }
        // NOTE: Just to run canceling user account and unassigning device requests in parallel
        yield Promise.all(clearRegistrationDataCalls);
    }), [unassignDeviceFromGuest, cancelUserAccount]);
    const registerDeviceAndRedirectToProfile = useCallback((registrationCode, createdUserData, offlineRegistrationData) => __awaiter(void 0, void 0, void 0, function* () {
        let registrationResult;
        const preparedDeviceCode = removeSpacesFromCode(registrationCode);
        if (isDeviceRegistrationMandatory) {
            registrationResult = yield assignDeviceToUser(preparedDeviceCode, createdUserData.ciam_token);
        }
        else {
            registrationResult = yield registerDevice(preparedDeviceCode, getUsersToken());
        }
        dispatchTrackingEvent(TrackingEvents.TrackingUserLoginDeviceRegister, {
            device_reference: registrationResult.data.productId || registrationResult.data.deviceId,
            cta_name: __("auth.registration.device.button_register_device"),
        });
        proceedWithRegistration(offlineRegistrationData);
    }), [__, assignDeviceToUser, isDeviceRegistrationMandatory, proceedWithRegistration, registerDevice]);
    const setUser = useCallback((userData) => {
        const user = new User(new BrowserStorage());
        user.setUser(userData);
    }, []);
    const regularRegistration = useCallback((data) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c;
        let loggedInUserLocal = undefined;
        try {
            setIsSubmitting(true);
            if (isDeviceRegistrationMandatory && !!data.registrationCode) {
                yield registerDeviceToGuest(removeSpacesFromCode(data.registrationCode), data.email);
            }
            if (data.streetName && data.streetNumber) {
                data.custom_attributes.street = [data.streetName, data.streetNumber, (data === null || data === void 0 ? void 0 : data.streetAdditional) || ""];
            }
            const requestObjectCasObject = transformToCasUser(data, getCurrentLanguage(languages));
            const ageVerificationUuidFromCookie = getAgeVerificationUuid();
            const isForceOfflineAv = getIsOfflineRegistrationAvForced();
            if (isAgeVerificationHidden && !isAgeVerificationUuidFromCookieRequired && !isForceOfflineAv) {
                try {
                    const { uuid: age_verification_uuid, is_verified } = yield verify({
                        user_email: data.email,
                        country_code: config.ISO_COUNTRY_CODE,
                        method_code: PERSONAL_INFORMATION,
                        fields: prepareFieldsForAV(requestObjectCasObject, data, additionalFields),
                    });
                    if (age_verification_uuid) {
                        requestObjectCasObject.age_verification_uuid = age_verification_uuid;
                    }
                    if (is_verified) {
                        messageBus.publish("tracking.event", {
                            payload: {
                                event_name: "hav_start",
                                additionalData: {
                                    hav_method: "hidden_age_verification",
                                },
                            },
                        });
                        messageBus.publish("tracking.event", {
                            payload: {
                                event_name: "hav_success",
                                additionalData: {
                                    hav_method: "hidden_age_verification",
                                },
                            },
                        });
                    }
                }
                catch (error) {
                    console.error(error);
                }
            }
            if (isAgeVerificationUuidFromCookieRequired) {
                if (!ageVerificationUuidFromCookie) {
                    throw new Error(__("auth.age_verification.error.missing_uuid"));
                }
                requestObjectCasObject.age_verification_uuid = ageVerificationUuidFromCookie;
            }
            yield register(requestObjectCasObject);
            setIsUserCreated(true);
            const userData = yield login({ email: data.email, password: data.password });
            setUser(userData);
            dispatchTrackingEvent(TrackingEvents.TrackingSignUpRegular, {
                from_kakao: registrationFlowName === "kakao" ? 1 : 0,
            });
            yield getUserProfile();
            loggedInUserLocal = userData;
            let offlineDobData = "";
            if (config.dobFormType !== "inputs" &&
                config.dobFormType !== "inputs_reversed" &&
                typeof data.dob.dobYear !== "string" &&
                typeof data.dob.dobMonth !== "string" &&
                typeof data.dob.dobDay !== "string") {
                offlineDobData = `${(_a = data.dob.dobYear) === null || _a === void 0 ? void 0 : _a.value}-${(_b = data.dob.dobMonth) === null || _b === void 0 ? void 0 : _b.value}-${(_c = data.dob.dobDay) === null || _c === void 0 ? void 0 : _c.value}`;
            }
            else {
                offlineDobData = `${data.dob.dobYear}-${data.dob.dobMonth}-${data.dob.dobDay}`;
            }
            const offlineRegistrationData = {
                firstname: data.firstname,
                lastname: data.lastname,
                dob: offlineDobData,
                userId: userData.uuid,
                email: data.email,
            };
            if (data.registrationCode) {
                yield registerDeviceAndRedirectToProfile(data.registrationCode, userData, offlineRegistrationData);
                return;
            }
            proceedWithRegistration(offlineRegistrationData);
        }
        catch (error) {
            showToastError(error);
            isDeviceRegistrationMandatory &&
                clearRegistrationAssignments({
                    deviceId: data.registrationCode,
                    consumerEmail: data.email,
                    loggedInUser: loggedInUserLocal,
                });
        }
        finally {
            setIsSubmitting(false);
        }
    }), [
        isDeviceRegistrationMandatory,
        languages,
        isAgeVerificationHidden,
        isAgeVerificationUuidFromCookieRequired,
        register,
        login,
        setUser,
        getUserProfile,
        config.dobFormType,
        config.ISO_COUNTRY_CODE,
        proceedWithRegistration,
        registerDeviceToGuest,
        verify,
        additionalFields,
        messageBus,
        __,
        registerDeviceAndRedirectToProfile,
        clearRegistrationAssignments,
    ]);
    const kakaoRegister = useCallback((data) => __awaiter(void 0, void 0, void 0, function* () {
        const passVerifiedDateOfBirth = getPassVerifiedData(PASS_VERIFIED_DATE_OF_BIRTH_COOKIE);
        const passVerifiedName = getPassVerifiedData(PASS_VERIFIED_NAME);
        const passVerifiedPhone = getPassVerifiedData(PASS_VERIFIED_PHONE_COOKIE);
        const passVerifiedUuid = getAgeVerificationUuid();
        try {
            if (typeof passVerifiedDateOfBirth !== "string") {
                throw new Error(__("auth.registration.kakao.error.dob"));
            }
            if (typeof passVerifiedName !== "string") {
                throw new Error(__("auth.registration.kakao.error.name"));
            }
            if (typeof passVerifiedPhone !== "string") {
                throw new Error(__("auth.registration.kakao.error.phone"));
            }
            if (!passVerifiedUuid) {
                throw new Error(__("auth.age_verification.error.missing_uuid"));
            }
            const registerData = Object.assign(Object.assign({ strategy: "kakao-credential", strategy_data: { code: data.code } }, data), { date_of_birth: passVerifiedDateOfBirth, age_verification_uuid: passVerifiedUuid, lastname: passVerifiedName, phone: passVerifiedPhone });
            const { authentication: userDataAfterLogin } = yield register(registerData, ISO_DATE_FORMAT);
            if (userDataAfterLogin) {
                setUser(userDataAfterLogin);
                dispatchTrackingEvent(TrackingEvents.TrackingSignUpRegular, {
                    from_kakao: registrationFlowName === "kakao" ? 1 : 0,
                });
                proceedWithRegistration();
            }
        }
        catch (error) {
            showToastError(error);
        }
    }), [__, proceedWithRegistration, register, setUser]);
    const flowRegistration = {
        kakao: kakaoRegister,
        regular: regularRegistration,
    };
    return { form, register: flowRegistration[registrationFlowName], isSubmitting, isUserCreated, pushToUserQrCode };
};
